import { Button, Grid, Text, TextField } from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
// eslint-disable-next-line import/named
import { Link as RouterLink, useSearch } from "@tanstack/react-router";
import cx from "classnames";
import { type FormEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoginLayout } from "../../components/layouts/login";
import { sendPasswordResetEmail } from "../../services/auth";
import { loginRoute } from "../login/route";

function useForgotPasswordMutation() {
  const [errorMessage, setErrorMessage] = useState<string>("");

  const forgotPasswordMutation = useMutation({
    mutationKey: ["forgot-password"],
    mutationFn: sendPasswordResetEmail,
    onError: async (error: Response) => {
      // why: smartFetch throws the Response on !ok but a raw TypeError on a
      // network failure, and an error body is not always JSON (gateway HTML,
      // 502/504). `error.json()` then rejects inside this async handler, so the
      // rejection escapes unhandled while `mutation.isError` renders an empty
      // alert. Fall back to generic copy when the body cannot be read.
      try {
        const data = (await error.json()) as { message?: string };
        setErrorMessage(
          data?.message ?? "Something went wrong. Please try again.",
        );
      } catch {
        setErrorMessage("Something went wrong. Please try again.");
      }
    },
  });

  return { ...forgotPasswordMutation, errorMessage };
}

export function ForgotPasswordView() {
  const { t } = useTranslation("auth");
  const { errorMessage, ...mutation } = useForgotPasswordMutation();
  const search = useSearch({ strict: false });

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form as HTMLFormElement);

    mutation.mutate(formData.get("email") as string);
  };

  if (mutation.isSuccess) {
    return (
      <LoginLayout>
        <Text as="p" size="6" weight="medium" className="mb-4 tracking-tight">
          {t("forgotPassword.successMessage")}
        </Text>

        <RouterLink
          to={loginRoute.to}
          search={search.redirect ? { redirect: search.redirect } : undefined}
          data-testid="login-existing-link"
          className="w-max"
        >
          <Text size="2" className="puntt-link underline-offset-4">
            <span className="font-medium underline decoration-puntt-accent-5">
              {t("forgotPassword.logInLink")}
            </span>{" "}
            {t("forgotPassword.toAnotherAccount")}
          </Text>
        </RouterLink>
      </LoginLayout>
    );
  }

  return (
    <LoginLayout>
      <Text
        weight="medium"
        size="6"
        className="mb-2 block tracking-tight text-base-black"
        data-testid="reset-password-description"
      >
        {t("forgotPassword.title")}
      </Text>

      <form
        onSubmit={handleSubmit}
        className="grid gap-4 sm:mb-4 sm:gap-6"
        data-testid="forgot-password-form"
        aria-label={t("forgotPassword.formAriaLabel")}
      >
        <Grid gap="3">
          <Text weight="medium" data-testid="email-field-label">
            {t("forgotPassword.workEmailLabel")}
          </Text>
          <TextField.Root
            required
            name="email"
            id="email-input"
            placeholder={t("forgotPassword.emailPlaceholder")}
            type="email"
            autoComplete="true"
            disabled={mutation.isPending}
            autoFocus
            size="2"
            data-testid="email-field"
          />
        </Grid>

        <Text
          color="red"
          size="2"
          data-testid="mfa-error-message"
          className={cx({ hidden: !mutation.isError })}
        >
          {errorMessage}
        </Text>

        <Button
          type="submit"
          disabled={mutation.isPending}
          loading={mutation.isPending}
          className="mt-2 [&>span]:justify-center"
          data-testid="form-submit-cta"
        >
          {t("forgotPassword.resetPasswordButton")}
        </Button>
      </form>

      <RouterLink
        to={loginRoute.to}
        search={search.redirect ? { redirect: search.redirect } : undefined}
        data-testid="login-existing-link"
        className="w-max"
      >
        <Text size="2" className="puntt-link underline-offset-4">
          <span className="underline decoration-puntt-accent-5">
            {t("forgotPassword.logInLink")}
          </span>{" "}
          {t("forgotPassword.toAnotherAccount")}
        </Text>
      </RouterLink>
    </LoginLayout>
  );
}
